import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Store, Search, RefreshCw, Loader2, Users, DollarSign,
  Star, TrendingUp, Eye, Crown
} from 'lucide-react';
import { toast } from 'sonner';

const AdminMarketplace = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ bots: 0, providers: 0, totalSubscribers: 0 });

  useEffect(() => {
    fetchMarketplace();
  }, []);

  const fetchMarketplace = async () => {
    setLoading(true);
    try {
      const [botsRes, providersRes] = await Promise.all([
        axios.get('/marketplace/bots'),
        axios.get('/marketplace/signal-providers')
      ]);
      
      // Bot response is {bots: [...], total: X, ...}
      const bots = botsRes.data?.bots || botsRes.data || [];
      const providers = providersRes.data || [];
      
      const allItems = [
        ...bots.map(b => ({ ...b, type: 'bot' })),
        ...providers.map(p => ({ ...p, type: 'provider' }))
      ];
      
      setItems(allItems);
      setStats({
        bots: bots.length,
        providers: providers.length,
        totalSubscribers: allItems.reduce((sum, i) => sum + (i.subscribers_count || i.subscribers || 0), 0)
      });
    } catch (error) {
      console.error('Marketplace fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => 
    !searchQuery || 
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="h-6 w-6 text-primary" />
            Pazaryeri Yönetimi
          </h1>
          <p className="text-slate-400 text-sm">Botları ve sinyal sağlayıcılarını yönet</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchMarketplace}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Botlar</p>
                <p className="text-2xl font-bold text-blue-400">{stats.bots}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Sinyal Sağlayıcı</p>
                <p className="text-2xl font-bold text-amber-400">{stats.providers}</p>
              </div>
              <Crown className="h-8 w-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Toplam Abone</p>
                <p className="text-2xl font-bold text-green-400">{stats.totalSubscribers}</p>
              </div>
              <Users className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Bot veya sağlayıcı ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-brand-dark border-border/40"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pazaryeri ({filteredItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Store className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Pazaryerinde içerik bulunamadı</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.type === 'bot' ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                      {item.type === 'bot' ? (
                        <TrendingUp className="h-5 w-5 text-blue-400" />
                      ) : (
                        <Crown className="h-5 w-5 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {item.subscribers_count || 0} abone
                        </span>
                        {item.price !== undefined && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            ₺{item.price || 0}/ay
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={item.type === 'bot' ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}>
                      {item.type === 'bot' ? 'Bot' : 'Sağlayıcı'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMarketplace;
